import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  Card,
  CardContent,
  Button,
  IconButton,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Tabs,
  Tab,
  Alert,
  Snackbar,
  Avatar,
  ListItemAvatar,
  Grid,
} from "@mui/material";
import {
  ShoppingCart,
  Notifications,
  LocalShipping,
  Delete,
  MarkEmailRead,
  Info,
  CheckCircle,
  Error,
  Pending,
  TrendingUp,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useDashboardData } from "../hooks/useDashboardData";
import { useNotifications } from "../contexts/NotificationContext";
import { DashboardLayout } from "../app/layout/DashboardLayout";

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const { stats, recentOrders, loading, refetch } = useDashboardData();
  const {
    notifications,
    unreadCount,
    isLoading: notificationsLoading,
    refreshNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
  } = useNotifications();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [showErrorAlert, setShowErrorAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");

  // Otomatik güncelleme için useEffect
  useEffect(() => {
    // Sayfa yüklendiğinde ilk güncelleme
    refetch();
    refreshNotifications();

    // Her 10 saniyede bir güncelleme
    const interval = setInterval(() => {
      refetch();
      refreshNotifications();
    }, 10000);

    // Cleanup
    return () => clearInterval(interval);
  }, [refetch, refreshNotifications]);

  // Sayfa görünür olduğunda güncelleme
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        refetch();
        refreshNotifications();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [refetch, refreshNotifications]);

  // Role-based permissions
  const canApproveOrders = user?.role === "admin" || user?.role === "manager";
  const canPrepareOrders = user?.role === "admin" || user?.role === "warehouse";
  const canShipOrders = user?.role === "admin" || user?.role === "warehouse";
  const canViewInventory = user?.role === "admin" || user?.role === "warehouse";
  const canViewAdminPanel = user?.role === "admin";

  // Role-based data filtering
  const getFilteredOrders = () => {
    if (!recentOrders || recentOrders.length === 0) return [];

    switch (user?.role) {
      case "admin":
        return recentOrders;
      case "manager":
        return recentOrders;
      case "warehouse":
        return recentOrders.filter(
          (order) =>
            order.status === "approved" ||
            order.status === "assigned" ||
            order.status === "prepared" ||
            order.status === "in_transit" ||
            order.status === "delivered"
        );
      case "salesperson":
        // Satıcılar sadece kendi siparişlerini görsün
        return recentOrders.filter(
          (order) => order.salesperson === user.full_name
        );
      case "driver":
        return recentOrders.filter(
          (order) =>
            order.status === "assigned" ||
            order.status === "in_transit" ||
            order.status === "out_for_delivery" ||
            order.status === "delivered"
        );
      default:
        return recentOrders;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "pending":
        return "default";
      case "pending_approval":
        return "warning";
      case "approved":
        return "success";
      case "rejected":
        return "error";
      case "assigned":
        return "info";
      case "prepared":
        return "info";
      case "in_transit":
        return "secondary";
      case "in_production":
        return "info";
      case "ready_for_delivery":
        return "info";
      case "out_for_delivery":
        return "secondary";
      case "delivered":
        return "success";
      case "cancelled":
        return "error";
      default:
        return "default";
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "pending":
        return "Bekliyor";
      case "pending_approval":
        return "Onay Bekliyor";
      case "approved":
        return "Onaylandı";
      case "rejected":
        return "Reddedildi";
      case "assigned":
        return "Atandı";
      case "prepared":
        return "Hazırlandı";
      case "in_transit":
        return "Yolda";
      case "in_production":
        return "Üretimde";
      case "ready_for_delivery":
        return "Teslimata Hazır";
      case "out_for_delivery":
        return "Teslimatta";
      case "delivered":
        return "Teslim Edildi";
      case "cancelled":
        return "İptal Edildi";
      default:
        return status;
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case "order_created":
        return <ShoppingCart />;
      case "order_approved":
        return <CheckCircle />;
      case "order_shipped":
        return <LocalShipping />;
      case "order_delivered":
        return <CheckCircle />;
      case "order_cancelled":
        return <Error />;
      case "system":
        return <Info />;
      default:
        return <Notifications />;
    }
  };

  const getNotificationColor = (type: string) => {
    switch (type) {
      case "order_created":
        return "info";
      case "order_approved":
        return "success";
      case "order_shipped":
        return "secondary";
      case "order_delivered":
        return "success";
      case "order_cancelled":
        return "error";
      case "system":
        return "warning";
      default:
        return "default";
    }
  };

  const formatNotificationTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor(
      (now.getTime() - date.getTime()) / (1000 * 60)
    );

    if (diffInMinutes < 1) return "Az önce";
    if (diffInMinutes < 60) return `${diffInMinutes} dakika önce`;
    if (diffInMinutes < 1440)
      return `${Math.floor(diffInMinutes / 60)} saat önce`;
    return `${Math.floor(diffInMinutes / 1440)} gün önce`;
  };

  const handleNotificationClick = async (notification: any) => {
    try {
      await markAsRead(notification.id);
      refreshNotifications();

      // Navigate based on notification type
      if (
        notification.type === "order_created" ||
        notification.type === "order_approved"
      ) {
        navigate("/orders");
      }
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
    }
  };

  const handleDeleteNotification = async (notificationId: string) => {
    try {
      await deleteNotification(notificationId);
      setAlertMessage("Bildirim silindi");
      setShowSuccessAlert(true);
      refreshNotifications();
    } catch (error) {
      setAlertMessage("Bildirim silinirken hata oluştu");
      setShowErrorAlert(true);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead();
      setAlertMessage("Tüm bildirimler okundu olarak işaretlendi");
      setShowSuccessAlert(true);
      refreshNotifications();
    } catch (error) {
      setAlertMessage("Bildirimler işaretlenirken hata oluştu");
      setShowErrorAlert(true);
    }
  };

  // Role-based action handlers
  const handleApproveOrder = async (orderId: string) => {
    if (!canApproveOrders) return;

    try {
      const { ordersAPI } = await import("../services/api");
      await ordersAPI.updateOrderStatus(orderId, { status: "approved" });
      setAlertMessage("Sipariş onaylandı");
      setShowSuccessAlert(true);
      refetch(); // Refresh dashboard data
    } catch (error) {
      setAlertMessage("Sipariş onaylanırken hata oluştu");
      setShowErrorAlert(true);
    }
  };

  const handleRejectOrder = async (orderId: string) => {
    if (!canApproveOrders) return;

    try {
      const { ordersAPI } = await import("../services/api");
      await ordersAPI.updateOrderStatus(orderId, { status: "cancelled" });
      setAlertMessage("Sipariş reddedildi");
      setShowSuccessAlert(true);
      refetch(); // Refresh dashboard data
    } catch (error) {
      setAlertMessage("Sipariş reddedilirken hata oluştu");
      setShowErrorAlert(true);
    }
  };

  const handlePrepareOrder = async (orderId: string) => {
    if (!canPrepareOrders) return;

    try {
      const { ordersAPI } = await import("../services/api");
      await ordersAPI.updateOrderStatus(orderId, { status: "prepared" });
      setAlertMessage("Sipariş hazırlandı");
      setShowSuccessAlert(true);
      refetch(); // Refresh dashboard data
    } catch (error) {
      setAlertMessage("Sipariş hazırlanırken hata oluştu");
      setShowErrorAlert(true);
    }
  };

  const handleShipOrder = async (orderId: string) => {
    if (!canShipOrders) return;

    try {
      const { ordersAPI } = await import("../services/api");
      await ordersAPI.updateOrderStatus(orderId, { status: "in_transit" });
      setAlertMessage("Sipariş kargoya verildi");
      setShowSuccessAlert(true);
      refetch(); // Refresh dashboard data
    } catch (error) {
      setAlertMessage("Sipariş kargoya verilirken hata oluştu");
      setShowErrorAlert(true);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("tr-TR", {
      style: "currency",
      currency: "TRY",
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("tr-TR", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleCloseSuccessAlert = () => {
    setShowSuccessAlert(false);
  };

  const handleCloseErrorAlert = () => {
    setShowErrorAlert(false);
  };

  // Dashboard metrics based on role
  const getDashboardMetrics = () => {
    if (!stats) {
      return {
        totalOrders: 0,
        pendingOrders: 0,
        approvedOrders: 0,
        inTransitOrders: 0,
        deliveredOrders: 0,
        totalRevenue: 0,
      };
    }

    const metrics = {
      totalOrders: stats.total_orders || 0,
      pendingOrders: stats.pending_orders || 0,
      approvedOrders: stats.approved_orders || 0,
      inTransitOrders: stats.in_transit_orders || 0,
      deliveredOrders: stats.completed_orders || 0,
      totalRevenue: stats.total_revenue || 0,
    };

    return metrics;
  };

  const metrics = getDashboardMetrics();

  return (
    <DashboardLayout
      title={user?.role === "salesperson" ? "Siparişlerim" : "Dashboard"}
    >
      <Container maxWidth="xl">
        {/* Success/Error Alerts */}
        <Snackbar
          open={showSuccessAlert}
          autoHideDuration={6000}
          onClose={handleCloseSuccessAlert}
        >
          <Alert onClose={handleCloseSuccessAlert} severity="success">
            {alertMessage}
          </Alert>
        </Snackbar>

        <Snackbar
          open={showErrorAlert}
          autoHideDuration={6000}
          onClose={handleCloseErrorAlert}
        >
          <Alert onClose={handleCloseErrorAlert} severity="error">
            {alertMessage}
          </Alert>
        </Snackbar>

        {/* Dashboard Metrics */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Box>
                    <Typography color="textSecondary" gutterBottom>
                      Toplam Sipariş
                    </Typography>
                    <Typography variant="h4">{metrics.totalOrders}</Typography>
                  </Box>
                  <ShoppingCart color="primary" sx={{ fontSize: 40 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Box>
                    <Typography color="textSecondary" gutterBottom>
                      Bekleyen Sipariş
                    </Typography>
                    <Typography variant="h4" color="warning.main">
                      {metrics.pendingOrders}
                    </Typography>
                  </Box>
                  <Pending color="warning" sx={{ fontSize: 40 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Box>
                    <Typography color="textSecondary" gutterBottom>
                      Yolda
                    </Typography>
                    <Typography variant="h4" color="info.main">
                      {metrics.inTransitOrders}
                    </Typography>
                  </Box>
                  <LocalShipping color="info" sx={{ fontSize: 40 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Box>
                    <Typography color="textSecondary" gutterBottom>
                      Toplam Gelir
                    </Typography>
                    <Typography variant="h4" color="success.main">
                      {formatCurrency(metrics.totalRevenue)}
                    </Typography>
                  </Box>
                  <TrendingUp color="success" sx={{ fontSize: 40 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Main Content Tabs */}
        <Paper sx={{ width: "100%" }}>
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            sx={{ borderBottom: 1, borderColor: "divider" }}
          >
            <Tab label="Siparişler" />
            <Tab label="Bildirimler" />
            {canViewInventory && <Tab label="Envanter" />}
            {canViewAdminPanel && <Tab label="Yönetim" />}
          </Tabs>

          {/* Orders Tab */}
          {activeTab === 0 && (
            <Box sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Siparişler
              </Typography>

              {loading ? (
                <Typography>Yükleniyor...</Typography>
              ) : (
                <List>
                  {getFilteredOrders()
                    .slice(0, 10)
                    .map((order) => (
                      <ListItem key={order.id} divider>
                        <ListItemAvatar>
                          <Avatar>
                            <ShoppingCart />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={`Sipariş #${order.id}`}
                          secondary={
                            <Box>
                              <Typography variant="body2" color="textSecondary">
                                {order.customerName} • {formatDate(order.date)}
                              </Typography>
                              <Typography variant="body2">
                                {formatCurrency(order.totalAmount)}
                              </Typography>
                            </Box>
                          }
                        />
                        <Box display="flex" alignItems="center" gap={1}>
                          <Chip
                            label={getStatusLabel(order.status)}
                            color={getStatusColor(order.status) as any}
                            size="small"
                          />

                          {/* Role-based action buttons */}
                          {canApproveOrders &&
                            order.status === "pending_approval" && (
                              <>
                                <Button
                                  size="small"
                                  variant="contained"
                                  color="success"
                                  onClick={() => handleApproveOrder(order.id)}
                                >
                                  Onayla
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="error"
                                  onClick={() => handleRejectOrder(order.id)}
                                >
                                  Reddet
                                </Button>
                              </>
                            )}

                          {canPrepareOrders && order.status === "approved" && (
                            <Button
                              size="small"
                              variant="contained"
                              color="info"
                              onClick={() => handlePrepareOrder(order.id)}
                            >
                              Hazırla
                            </Button>
                          )}

                          {canShipOrders && order.status === "prepared" && (
                            <Button
                              size="small"
                              variant="contained"
                              color="secondary"
                              onClick={() => handleShipOrder(order.id)}
                            >
                              Kargoya Ver
                            </Button>
                          )}
                        </Box>
                      </ListItem>
                    ))}
                </List>
              )}
            </Box>
          )}

          {/* Notifications Tab */}
          {activeTab === 1 && (
            <Box sx={{ p: 3 }}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
              >
                <Typography variant="h6">
                  Bildirimler {unreadCount > 0 && `(${unreadCount})`}
                </Typography>
                {unreadCount > 0 && (
                  <Button
                    startIcon={<MarkEmailRead />}
                    onClick={handleMarkAllAsRead}
                    size="small"
                  >
                    Tümünü Okundu İşaretle
                  </Button>
                )}
              </Box>

              {notificationsLoading ? (
                <Typography>Yükleniyor...</Typography>
              ) : (
                <List>
                  {notifications.slice(0, 10).map((notification) => (
                    <ListItem key={notification.id} divider>
                      <ListItemIcon>
                        <Avatar
                          sx={{
                            bgcolor: `${getNotificationColor(notification.type)}.main`,
                          }}
                        >
                          {getNotificationIcon(notification.type)}
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={notification.title}
                        secondary={
                          <Box>
                            <Typography variant="body2" color="textSecondary">
                              {notification.message}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {formatNotificationTime(notification.created_at)}
                            </Typography>
                          </Box>
                        }
                      />
                      <Box display="flex" gap={1}>
                        <IconButton
                          size="small"
                          onClick={() => handleNotificationClick(notification)}
                        >
                          <Info />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() =>
                            handleDeleteNotification(notification.id)
                          }
                        >
                          <Delete />
                        </IconButton>
                      </Box>
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          )}

          {/* Inventory Tab - Only for warehouse and admin */}
          {activeTab === 2 && canViewInventory && (
            <Box sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Envanter Yönetimi
              </Typography>
              <Typography color="textSecondary">
                Envanter yönetimi özellikleri burada görüntülenecek.
              </Typography>
            </Box>
          )}

          {/* Admin Panel Tab - Only for admin */}
          {activeTab === 3 && canViewAdminPanel && (
            <Box sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Yönetim Paneli
              </Typography>
              <Typography color="textSecondary">
                Yönetim paneli özellikleri burada görüntülenecek.
              </Typography>
            </Box>
          )}
        </Paper>
      </Container>
    </DashboardLayout>
  );
};

export default Dashboard;
